// Account and profile callables.
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/** Permanently delete the signed-in user's account and competitive data (Admin SDK). */
export async function deleteAccount(): Promise<{ ok: true }> {
  const call = httpsCallable<Record<string, never>, { ok: true }>(functions, 'deleteAccount');
  return (await call({})).data;
}

/**
 * Finish the profile step and atomically claim the @username. Uniqueness is
 * enforced server-side (a reservation transaction), so the client never writes
 * the username field directly.
 */
export interface CompleteProfileInput {
  displayName: string;
  username: string;
  avatarTone?: number;
  /** Download URL of an already-uploaded profile photo, if the player set one. */
  photoURL?: string;
}
export async function completeProfile(input: CompleteProfileInput): Promise<{ ok: true; username: string }> {
  const call = httpsCallable<CompleteProfileInput, { ok: true; username: string }>(functions, 'completeProfile');
  return (await call(input)).data;
}

/**
 * Update mutable profile fields (display name, avatar). The display name is screened
 * server-side by the shared objectionable-content filter, so name changes go through
 * this callable rather than a direct client write (the @username is immutable).
 */
export interface UpdateProfileInput {
  displayName?: string;
  avatarTone?: number;
  photoURL?: string;
}
export async function updateProfile(input: UpdateProfileInput): Promise<{ ok: true }> {
  const call = httpsCallable<UpdateProfileInput, { ok: true }>(functions, 'updateProfile');
  return (await call(input)).data;
}

/**
 * Hand the server the one-time Sign in with Apple authorization code so it can later
 * revoke the Apple token when the account is deleted (Guideline 5.1.1(v)). Best-effort
 * and silent — never block sign-in on it.
 */
export async function registerAppleCredential(authorizationCode: string): Promise<{ ok: true }> {
  const call = httpsCallable<{ authorizationCode: string }, { ok: true }>(functions, 'registerAppleCredential');
  return (await call({ authorizationCode })).data;
}
